/* eslint-disable camelcase */
import { useContext } from 'react';

import { createClientEvent, GLOBAL_NAMESPACE as CET_NAMESPACE } from '@buzzfeed/client-event-tracking';
import {
  ab_test,
  content_action,
  external_link,
  impression,
  internal_link,
  pageview,
  time_spent
} from '@buzzfeed/client-event-tracking/events';
import { createClientTimeSpentHandler, createClientClickHandler, createClientImpressionHandler } from '@buzzfeed/client-event-tracking/handlers';
import { TrackingContext } from '../../contexts';
import { CLUSTER } from '../../constants';

const sendClientEvent = createClientEvent({
  env: CLUSTER,
  source: 'web_island',
});

const createClientEventWithCommonData = event_config => (...layers) => {
  sendClientEvent(
    event_config,
    ...layers
  );
};

const trackClientImpression = createClientEventWithCommonData(impression);
const trackClientExternalLink = createClientEventWithCommonData(external_link);
const trackClientInternalLink = createClientEventWithCommonData(internal_link);
const trackClientContentAction = createClientEventWithCommonData(
  content_action
);

const attachClientInternalLinkHandler = createClientClickHandler(
  ({ layers }, ...additional_args) => {
    trackClientInternalLink(...additional_args, ...layers);
  }
);

const attachClientExternalLinkHandler = createClientClickHandler(
  ({ layers }, ...additional_args) => {
    trackClientExternalLink(...additional_args, ...layers);
  }
);

const attachClientImpressionHandler = createClientImpressionHandler(
  trackClientImpression
);

const trackClientPageview = createClientEventWithCommonData(pageview);
const trackClientTimeSpent = createClientEventWithCommonData(time_spent);
const trackClientExperimentActive = createClientEventWithCommonData(ab_test);

const initializeClientTimeHandler = createClientTimeSpentHandler(
    ({ layers }, ...additional_args) => {
      trackClientTimeSpent(...additional_args, ...layers);
    }
);

export const useTrackingContext = () => {
  const trackingPageInfo = useContext(TrackingContext);

  const withPageInfoClient = trackingAction => (element, data) => {
    const commonData = {
      destination: 'island',
      page_edition: 'en-us',
      referrer_uri: window?.[CET_NAMESPACE]?.referrer || document.referrer,
    };
    return trackingAction(element, { ...commonData, ...trackingPageInfo }, data);
  }

  return {
    trackImpression: withPageInfoClient(trackClientImpression),
    trackExternalLink: withPageInfoClient(trackClientExternalLink),
    trackInternalLink: withPageInfoClient(trackClientInternalLink),
    trackContentAction: withPageInfoClient(trackClientContentAction),
    trackPageview: withPageInfoClient(trackClientPageview),
    trackExperimentActive: withPageInfoClient(trackClientExperimentActive),
    trackTimeSpent: withPageInfoClient(initializeClientTimeHandler),
    trackInternalLinkHandler: withPageInfoClient(attachClientInternalLinkHandler),
    trackExternalLinkHandler: withPageInfoClient(attachClientExternalLinkHandler),
    trackImpressionHandler: withPageInfoClient(attachClientImpressionHandler),
  };
};
